import React, { useState, useEffect, useCallback, useMemo } from "react";
import "./Collab.css";
import "./loader.css";
import { FaPaperPlane, FaEllipsisV, FaTimes, FaSearch } from "react-icons/fa";
import { supabase } from "../supabaseClient";

export default function Collab({ onClose, user }) {
  const [emailConvite, setEmailConvite] = useState("");
  const [integrantes, setIntegrantes] = useState([]);
  const [enviando, setEnviando] = useState(false);
  const [menuAberto, setMenuAberto] = useState(null);
  const [removendo, setRemovendo] = useState(null);
  const [loadingIntegrantes, setLoadingIntegrantes] = useState(true);
  const [buscaColaborador, setBuscaColaborador] = useState("");

  const [allProjects, setAllProjects] = useState([]);
  const [allSetores, setAllSetores] = useState([]);
  const [selectedPermissions, setSelectedPermissions] = useState({ projetos: [], setores: [] });
  const [loadingProjectsSetores, setLoadingProjectsSetores] = useState(true);

  // === Novos estados para o fluxo de reatribuição de atividades ===
  const [removendoAlvo, setRemovendoAlvo] = useState(null);
  const [atividadesAfetadas, setAtividadesAfetadas] = useState([]);
  const [carregandoAtividades, setCarregandoAtividades] = useState(false);
  const [novoResponsavelId, setNovoResponsavelId] = useState("nenhum");
  const [confirmandoReatribuicao, setConfirmandoReatribuicao] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e) => { if (e.key === "Escape") onClose(); };
    const handleClickOutside = (e) => {
      if (e.target.classList.contains("collab-modal-overlay")) onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onClose]);

  const fetchIntegrantes = useCallback(async () => {
    if (!user?.id) { setIntegrantes([]); setLoadingIntegrantes(false); return; }
    setLoadingIntegrantes(true);
    try {
      const { data: convitesAceitos, error } = await supabase
        .from("convites")
        .select("*")
        .eq("remetente_id", user.id)
        .eq("status", "aceito");

      if (error) { console.error("Erro ao buscar integrantes:", error); setIntegrantes([]); return; }

      const integrantesComPerfil = await Promise.all(
        (convitesAceitos || []).map(async (c) => {
          const { data: profile } = await supabase
            .from("profiles")
            .select("id, nome, email, avatar_url")
            .ilike("email", c.email)
            .maybeSingle();
          return {
            convite_id: c.id,
            invited_email: c.email,
            profile_id: profile?.id || null,
            nome: profile?.nome || c.email,
            email: profile?.email || c.email,
            avatar_url: profile?.avatar_url || null,
            permissoes: { projetos: [], setores: [] },
          };
        })
      );

      const colaboradorIds = integrantesComPerfil.map((i) => i.profile_id).filter(Boolean);
      if (colaboradorIds.length > 0) {
        const { data: permissoesData, error: permError } = await supabase
          .from("permissoes_colaboradores")
          .select("colaborador_id, projeto_id, setor_id")
          .eq("container_id", user.id)
          .in("colaborador_id", colaboradorIds);

        if (!permError) {
          const permissoesPorColaborador = {};
          (permissoesData || []).forEach((p) => {
            if (!permissoesPorColaborador[p.colaborador_id]) {
              permissoesPorColaborador[p.colaborador_id] = { projetos: [], setores: [] };
            }
            if (p.projeto_id) permissoesPorColaborador[p.colaborador_id].projetos.push(p.projeto_id);
            if (p.setor_id) permissoesPorColaborador[p.colaborador_id].setores.push(p.setor_id);
          });
          integrantesComPerfil.forEach((i) => {
            i.permissoes = permissoesPorColaborador[i.profile_id] || { projetos: [], setores: [] };
          });
        } else {
          console.error("Erro ao buscar permissões dos integrantes:", permError);
        }
      }

      setIntegrantes(integrantesComPerfil);
    } catch (err) {
      console.error("Erro ao buscar integrantes:", err);
      setIntegrantes([]);
    } finally {
      setLoadingIntegrantes(false);
    }
  }, [user?.id]);

  const fetchProjectsAndSetores = useCallback(async () => {
    if (!user?.id) { setAllProjects([]); setAllSetores([]); setLoadingProjectsSetores(false); return; }
    setLoadingProjectsSetores(true);
    try {
      const { data: proj } = await supabase.from("projects").select("id, name").eq("user_id", user.id);
      setAllProjects(proj || []);
      const { data: set } = await supabase.from("setores").select("id, name").eq("user_id", user.id);
      setAllSetores(set || []);
    } catch (err) {
      console.error("Erro ao carregar projetos e setores:", err);
    } finally {
      setLoadingProjectsSetores(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchIntegrantes();
    fetchProjectsAndSetores();
  }, [fetchIntegrantes, fetchProjectsAndSetores]);

  const nomeProjetoPorId = useMemo(() => {
    const map = {};
    allProjects.forEach((p) => { map[p.id] = p.name; });
    return map;
  }, [allProjects]);

  const nomeSetorPorId = useMemo(() => {
    const map = {};
    allSetores.forEach((s) => { map[s.id] = s.name; });
    return map;
  }, [allSetores]);

  const integrantesExibidos = useMemo(() => {
    const termo = buscaColaborador.trim().toLowerCase();
    return integrantes
      .filter((i) => {
        if (!termo) return true;

        const nomesProjetos = i.permissoes.projetos.map((id) => nomeProjetoPorId[id]).filter(Boolean);
        const nomesSetores = i.permissoes.setores.map((id) => nomeSetorPorId[id]).filter(Boolean);

        const bateNoColaborador =
          i.nome.toLowerCase().includes(termo) || i.email.toLowerCase().includes(termo);
        const bateNoProjeto = nomesProjetos.some((nome) => nome.toLowerCase().includes(termo));
        const bateNoSetor = nomesSetores.some((nome) => nome.toLowerCase().includes(termo));

        return bateNoColaborador || bateNoProjeto || bateNoSetor;
      })
      .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR", { sensitivity: "base" }));
  }, [integrantes, buscaColaborador, nomeProjetoPorId, nomeSetorPorId]);

  const enviarConvite = async () => {
    if (!emailConvite.trim()) return alert("Digite um e-mail válido.");
    setEnviando(true);
    try {
      const { data: profile } = await supabase
        .from("profiles")
        .select("id, nome, email, nickname")
        .ilike("email", emailConvite)
        .maybeSingle();

      if (!profile) { alert("Usuário não encontrado no OnBox."); setEnviando(false); return; }

      const { data: existingInvite } = await supabase
        .from("convites")
        .select("*")
        .eq("email", profile.email)
        .eq("remetente_id", user.id)
        .eq("status", "pendente")
        .maybeSingle();

      if (existingInvite) { alert("Convite já enviado."); setEnviando(false); return; }

      const { error: inviteError } = await supabase.from("convites").insert([{
        email: profile.email,
        remetente_id: user.id,
        user_id: profile.id,
        nickname: profile.nickname,
        container_id: user.id,
        status: "pendente",
      }]);
      if (inviteError) throw inviteError;

      const permissionsToInsert = [];
      selectedPermissions.projetos.forEach((projectId) => {
        permissionsToInsert.push({ colaborador_id: profile.id, container_id: user.id, projeto_id: projectId, setor_id: null });
      });
      selectedPermissions.setores.forEach((setorId) => {
        permissionsToInsert.push({ colaborador_id: profile.id, container_id: user.id, projeto_id: null, setor_id: setorId });
      });
      if (permissionsToInsert.length > 0) {
        const { error: permError } = await supabase.from("permissoes_colaboradores").insert(permissionsToInsert);
        if (permError) console.error("Erro ao salvar permissões prévias:", permError);
      }

      alert(`Convite enviado para ${profile.nome}`);
      setEmailConvite("");
      setSelectedPermissions({ projetos: [], setores: [] });
    } catch (err) {
      console.error("Erro ao enviar convite:", err);
      alert("Erro ao enviar convite.");
    } finally {
      setEnviando(false);
    }
  };

  // === Remoção direta (sem confirmação — quem confirma agora é o fluxo de iniciarRemocao) ===
  const removerIntegrante = async (item) => {
    try {
      setRemovendo(item.convite_id);
      await supabase.from("convites").delete().eq("id", item.convite_id);
      setIntegrantes((prev) => prev.filter((i) => i.convite_id !== item.convite_id));
      setMenuAberto(null);
      setRemovendo(null);
    } catch (err) {
      console.error("Erro ao remover integrante:", err);
      alert("Erro ao remover integrante.");
    }
  };

  // === Passo 1: verifica se a pessoa tem atividades vinculadas (nas 3 fontes) ===
  const iniciarRemocao = async (item) => {
    setMenuAberto(null);
    setCarregandoAtividades(true);
    try {
      const atividades = [];

      if (item.profile_id) {
        const { data: pilhasContainer } = await supabase
          .from("pilhas")
          .select("id")
          .eq("container_id", user.id);
        const pilhaIds = (pilhasContainer || []).map((p) => p.id);

        if (pilhaIds.length > 0) {
          const { data: notasDoContainer } = await supabase
            .from("notas")
            .select("id, nome")
            .in("pilha_id", pilhaIds);
          const notasContainerIds = (notasDoContainer || []).map((n) => n.id);
          const notasMap = (notasDoContainer || []).reduce((acc, n) => { acc[n.id] = n.nome; return acc; }, {});

          // 1) Notas Rápidas — responsaveis_ids array na própria nota
          const { data: notasRapidas } = await supabase
            .from("notas")
            .select("id, nome, pilha_id, responsaveis_ids")
            .in("pilha_id", pilhaIds)
            .contains("responsaveis_ids", [item.profile_id]);
          (notasRapidas || []).forEach((n) => {
            atividades.push({ tipo: "notaRapida", notaId: n.id, nome: n.nome });
          });

          // 2) Tarefas — responsáveis ficam na tabela nota_responsaveis
          if (notasContainerIds.length > 0) {
            const { data: respTarefas } = await supabase
              .from("nota_responsaveis")
              .select("id, nota_id")
              .eq("usuario_id", item.profile_id)
              .in("nota_id", notasContainerIds);
            (respTarefas || []).forEach((r) => {
              atividades.push({ tipo: "tarefa", notaId: r.nota_id, nome: notasMap[r.nota_id] || "Tarefa" });
            });
          }

          // 3) Objetivos de Ata — responsáveis ficam na tabela ata_objetivos_responsaveis
          const { data: respObjetivos } = await supabase
            .from("ata_objetivos_responsaveis")
            .select("id, ata_objetivo_id")
            .eq("usuario_id", item.profile_id);

          if (respObjetivos?.length > 0) {
            const objetivoIds = respObjetivos.map((r) => r.ata_objetivo_id);
            const { data: objetivosData } = await supabase
              .from("ata_objetivos")
              .select("id, texto, ata_id")
              .in("id", objetivoIds);

            const ataIds = [...new Set((objetivosData || []).map((o) => o.ata_id))];
            const { data: atasData } = ataIds.length
              ? await supabase.from("atas").select("id, nota_id").in("id", ataIds)
              : { data: [] };
            const ataNotaMap = (atasData || []).reduce((acc, a) => { acc[a.id] = a.nota_id; return acc; }, {});

            (objetivosData || []).forEach((o) => {
              const notaIdDaAta = ataNotaMap[o.ata_id];
              // só considera objetivos cuja ata pertence a uma nota deste container
              if (!notaIdDaAta || !notasContainerIds.includes(notaIdDaAta)) return;
              const respRow = respObjetivos.find((r) => r.ata_objetivo_id === o.id);
              atividades.push({
                tipo: "objetivoAta",
                objetivoId: o.id,
                respRowId: respRow?.id,
                nome: o.texto,
                notaNome: notasMap[notaIdDaAta] || "Ata",
              });
            });
          }
        }
      }

      if (atividades.length === 0) {
        const ok = window.confirm(`Remover ${item.nome} do seu container?`);
        if (ok) removerIntegrante(item);
        return;
      }

      setAtividadesAfetadas(atividades);
      setNovoResponsavelId("nenhum");
      setRemovendoAlvo(item);
    } finally {
      setCarregandoAtividades(false);
    }
  };

  // === Passo 2: aplica reatribuição (se houver) por tipo, e remove o integrante ===
  const confirmarRemocaoComReatribuicao = async () => {
    if (!removendoAlvo) return;
    setConfirmandoReatribuicao(true);
    try {
      const alvoId = removendoAlvo.profile_id;

      await Promise.all(
        atividadesAfetadas.map(async (ativ) => {
          // ── Notas Rápidas: array responsaveis_ids ──
          if (ativ.tipo === "notaRapida") {
            const { data: notaAtual } = await supabase
              .from("notas")
              .select("responsaveis_ids")
              .eq("id", ativ.notaId)
              .single();
            const semAlvo = (notaAtual?.responsaveis_ids || []).filter((id) => id !== alvoId);
            const novosIds = novoResponsavelId !== "nenhum"
              ? (semAlvo.includes(novoResponsavelId) ? semAlvo : [...semAlvo, novoResponsavelId])
              : semAlvo;
            // se "nenhum": não mexe (mantém alvoId na lista, gerando o avatar de sombra)
            if (novoResponsavelId !== "nenhum") {
              await supabase.from("notas").update({ responsaveis_ids: novosIds }).eq("id", ativ.notaId);
            }
          }

          // ── Tarefas: tabela nota_responsaveis ──
          if (ativ.tipo === "tarefa" && novoResponsavelId !== "nenhum") {
            const { data: existente } = await supabase
              .from("nota_responsaveis")
              .select("id")
              .eq("nota_id", ativ.notaId)
              .eq("usuario_id", novoResponsavelId)
              .maybeSingle();
            if (existente) {
              // o novo responsável já está na tarefa; só remove o vínculo antigo
              await supabase.from("nota_responsaveis").delete().eq("nota_id", ativ.notaId).eq("usuario_id", alvoId);
            } else {
              // transfere o vínculo para o novo responsável
              await supabase.from("nota_responsaveis").update({ usuario_id: novoResponsavelId }).eq("nota_id", ativ.notaId).eq("usuario_id", alvoId);
            }
          }
          // se "nenhum": não mexe — o vínculo antigo continua, e o Task.jsx mostrará
          // o avatar de sombra por ele não estar mais em colaboradoresAtivosIds

          // ── Objetivos de Ata: tabela ata_objetivos_responsaveis ──
          if (ativ.tipo === "objetivoAta" && ativ.respRowId && novoResponsavelId !== "nenhum") {
            const { data: existente } = await supabase
              .from("ata_objetivos_responsaveis")
              .select("id")
              .eq("ata_objetivo_id", ativ.objetivoId)
              .eq("usuario_id", novoResponsavelId)
              .maybeSingle();
            if (existente) {
              await supabase.from("ata_objetivos_responsaveis").delete().eq("id", ativ.respRowId);
            } else {
              await supabase.from("ata_objetivos_responsaveis").update({ usuario_id: novoResponsavelId }).eq("id", ativ.respRowId);
            }
          }
          // se "nenhum": não mexe — a linha permanece com usuario_id = alvoId, e o
          // AtaObjetivos.jsx já detecta isso via colaboradoresAtivosIds e mostra o chip de sombra
        })
      );

      await removerIntegrante(removendoAlvo);
      setRemovendoAlvo(null);
      setAtividadesAfetadas([]);
      setNovoResponsavelId("nenhum");
    } catch (err) {
      console.error("Erro ao reatribuir atividades:", err);
      alert("Erro ao transferir atividades. Tente novamente.");
    } finally {
      setConfirmandoReatribuicao(false);
    }
  };

  const togglePermission = (type, id) => {
    setSelectedPermissions((prev) => {
      const currentList = prev[type];
      const newList = currentList.includes(id) ? currentList.filter((item) => item !== id) : [...currentList, id];
      return { ...prev, [type]: newList };
    });
  };

  return (
    <>
      <div className="collab-modal-overlay">
        <div className="collab-modal">
          <div className="collab-header">
            <h2>Colaboração</h2>
            <button className="collab-close-btn" onClick={onClose} aria-label="Fechar">
              <FaTimes />
            </button>
          </div>

          <div className="collab-section-convite">
            <h4>Enviar Convite</h4>
            <div className="convite-form">
              <input
                type="email"
                placeholder="Digite o e-mail do colaborador..."
                value={emailConvite}
                onChange={(e) => setEmailConvite(e.target.value)}
              />
              <button
                className={`btn-enviar ${enviando ? "plane-fly" : ""}`}
                onClick={!enviando ? enviarConvite : undefined}
                disabled={enviando}
              >
                <FaPaperPlane className="plane-icon" />
                {!enviando && " Enviar"}
              </button>
            </div>

            {emailConvite.trim() && (
              <div className="permissoes-previas-section">
                <h4>Definir Permissões Prévias</h4>
                {loadingProjectsSetores ? (
                  <div className="loader-container"><div className="loader"></div></div>
                ) : (
                  <>
                    {allProjects.length > 0 && (
                      <div className="permissoes-group">
                        <h5>Projetos</h5>
                        <div className="checkbox-list">
                          {allProjects.map((proj) => (
                            <label key={proj.id} className="checkbox-item">
                              <input
                                type="checkbox"
                                checked={selectedPermissions.projetos.includes(proj.id)}
                                onChange={() => togglePermission("projetos", proj.id)}
                              />
                              <span>{proj.name}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                    {allSetores.length > 0 && (
                      <div className="permissoes-group">
                        <h5>Setores</h5>
                        <div className="checkbox-list">
                          {allSetores.map((setor) => (
                            <label key={setor.id} className="checkbox-item">
                              <input
                                type="checkbox"
                                checked={selectedPermissions.setores.includes(setor.id)}
                                onChange={() => togglePermission("setores", setor.id)}
                              />
                              <span>{setor.name}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                    {allProjects.length === 0 && allSetores.length === 0 && (
                      <p className="empty">Nenhum projeto ou setor disponível para permissões.</p>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          <hr />

          <div className="collab-section1">
            <h3>Convidados do meu container</h3>

            <div className="busca-colaborador-wrapper">
              <FaSearch className="busca-colaborador-icon" />
              <input
                type="text"
                className="busca-colaborador-input"
                placeholder="Buscar por colaborador, projeto ou setor..."
                value={buscaColaborador}
                onChange={(e) => setBuscaColaborador(e.target.value)}
              />
            </div>

            {loadingIntegrantes ? (
              <div className="loader-container"><div className="loader"></div></div>
            ) : integrantes.length === 0 ? (
              <p className="empty">Nenhum integrante ainda.</p>
            ) : integrantesExibidos.length === 0 ? (
              <p className="empty">Nenhum colaborador encontrado para "{buscaColaborador}".</p>
            ) : (
              <div className="integrantes-list">
                {integrantesExibidos.map((i) => {
                  const nomesProjetos = i.permissoes.projetos.map((id) => nomeProjetoPorId[id]).filter(Boolean);
                  const nomesSetores = i.permissoes.setores.map((id) => nomeSetorPorId[id]).filter(Boolean);
                  const semVinculo = nomesProjetos.length === 0 && nomesSetores.length === 0;

                  return (
                    <div key={i.convite_id} className={`integrante-item ${removendo === i.convite_id ? "fade-out" : ""}`}>
                      <div className="integrante-info">
                        {i.avatar_url ? (
                          <img src={i.avatar_url} alt={i.nome} className="integrante-avatar" />
                        ) : (
                          <div className="integrante-avatar placeholder">{i.nome.charAt(0).toUpperCase()}</div>
                        )}
                        <div className="integrante-texto">
                          <strong>{i.nome}</strong>
                          <span>{i.email}</span>
                          <div className="integrante-tags">
                            {semVinculo && <span className="tag-vazio">Sem vínculo definido</span>}
                            {nomesProjetos.map((nome) => (
                              <span key={`proj-${nome}`} className="tag-projeto">{nome}</span>
                            ))}
                            {nomesSetores.map((nome) => (
                              <span key={`setor-${nome}`} className="tag-setor">{nome}</span>
                            ))}
                          </div>
                        </div>
                      </div>
                      <div className="integrante-menu">
                        <button className="menu-btn" onClick={() => setMenuAberto(menuAberto === i.convite_id ? null : i.convite_id)}>
                          <FaEllipsisV />
                        </button>
                        {menuAberto === i.convite_id && (
                          <div className="menu-opcoes">
                            <button onClick={() => iniciarRemocao(i)} disabled={carregandoAtividades}>
                              Remover
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {removendoAlvo && (
        <div className="collab-modal-overlay reatribuicao-overlay">
          <div className="collab-modal reatribuicao-modal">
            <div className="collab-header">
              <h2>Transferir atividades</h2>
              <button
                className="collab-close-btn"
                onClick={() => setRemovendoAlvo(null)}
                aria-label="Fechar"
              >
                <FaTimes />
              </button>
            </div>

            <p>
              <strong>{removendoAlvo.nome}</strong> é responsável por{" "}
              {atividadesAfetadas.length}{" "}
              {atividadesAfetadas.length === 1 ? "atividade" : "atividades"} neste
              container. Antes de removê-la, escolha quem vai assumir essas
              atividades — ou deixe sem responsável.
            </p>

            <div className="reatribuicao-opcoes">
              <div
                className={`reatribuicao-opcao ${
                  novoResponsavelId === "nenhum" ? "reatribuicao-opcao--ativa" : ""
                }`}
                onClick={() => setNovoResponsavelId("nenhum")}
                title={`Usuário removido do container — ${removendoAlvo.nome}`}
              >
                <div className="reatribuicao-avatar reatribuicao-avatar--sombra">?</div>
                <span>Não atribuir ninguém</span>
              </div>

              {integrantes
                .filter((i) => i.convite_id !== removendoAlvo.convite_id && i.profile_id)
                .map((i) => (
                  <div
                    key={i.convite_id}
                    className={`reatribuicao-opcao ${
                      novoResponsavelId === i.profile_id ? "reatribuicao-opcao--ativa" : ""
                    }`}
                    onClick={() => setNovoResponsavelId(i.profile_id)}
                    title={i.nome}
                  >
                    {i.avatar_url ? (
                      <img src={i.avatar_url} alt={i.nome} className="reatribuicao-avatar" />
                    ) : (
                      <div className="reatribuicao-avatar reatribuicao-avatar--placeholder">
                        {i.nome.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <span>{i.nome}</span>
                  </div>
                ))}
            </div>

            <div className="reatribuicao-lista-atividades">
              <h5>Atividades afetadas</h5>
              <ul>
                {atividadesAfetadas.map((n, idx) => (
                  <li key={idx}>
                    <span className="reatribuicao-tipo-badge">
                      {n.tipo === "notaRapida" ? "Nota Rápida" : n.tipo === "tarefa" ? "Tarefa" : "Objetivo de Ata"}
                    </span>
                    {n.nome}
                  </li>
                ))}
              </ul>
            </div>

            <div className="reatribuicao-acoes">
              <button
                className="reatribuicao-btn-cancelar"
                onClick={() => setRemovendoAlvo(null)}
                disabled={confirmandoReatribuicao}
              >
                Cancelar
              </button>
              <button
                className="reatribuicao-btn-confirmar"
                onClick={confirmarRemocaoComReatribuicao}
                disabled={confirmandoReatribuicao}
              >
                {confirmandoReatribuicao ? "Processando..." : "Confirmar e remover"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}